import { useLayoutEffect, useRef } from 'react';

import BpkSmallSearchIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/search';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { useSelector } from 'react-redux';

import { LOW_PRICE_CLASS } from '../../../helpers/constants/priceBandConstants';
import * as validators from '../../../helpers/dateSelectionValidators';
import { useTranslation } from '../../../helpers/hooks/useTranslation';
import {
  getGridPriceData,
  getMaxDate,
  getMinCalendarDate,
  getPreferDirects,
  getPriceColourClass,
  hasDirectsForDate,
  isLoading,
} from '../../../selectors';

import type { Direction, State } from '../../../selectors/types';
import type { BpkCalendarDateProps } from '@skyscanner-internal/backpack-web/bpk-component-calendar';

import './PricedCalendarDate.scss';

type PriceFormatter = (price: number) => string;

type PricedCalendarDateProps = Omit<
  BpkCalendarDateProps,
  'modifiers' | 'ariaLabel'
> & {
  direction: Direction;
  priceFormatter: PriceFormatter;
  formatDateFull: (date: Date) => string;
  isKeyboardFocusable: boolean;
  isOutside: boolean;
  'aria-label'?: string;
};

type Modifiers = Record<string, (props: PricedCalendarDateProps) => boolean>;

const navigatedByMonthNudger = () =>
  document.activeElement?.id.includes('month_nudger');

const PricedCalendarDate = (props: PricedCalendarDateProps) => {
  const {
    date,
    direction,
    formatDateFull,
    isBlocked: _isBlocked,
    isFocused,
    isKeyboardFocusable,
    isOutside,
    isSelected,
    isToday,
    isoLabel,
    onClick,
    onDateKeyDown,
    preventKeyboardFocus,
    priceFormatter,
    selectionType,
    ...rest
  } = props;

  const translate = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (
      isFocused &&
      isKeyboardFocusable &&
      !preventKeyboardFocus &&
      !navigatedByMonthNudger()
    ) {
      buttonRef.current?.focus();
    }
  }, [isFocused, isKeyboardFocusable, preventKeyboardFocus]);

  const loading = useSelector((state: State) => isLoading(state));
  const minDate = useSelector((state: State) =>
    getMinCalendarDate(state, direction),
  );
  const maxDate = useSelector((state: State) => getMaxDate(state));
  const hasDirects = useSelector((state: State) =>
    hasDirectsForDate(state, date, direction),
  );
  const preferDirects = useSelector((state: State) => getPreferDirects(state));
  const priceFromGrid = useSelector((state: State) => {
    const gridPriceData = getGridPriceData(state, date, direction);
    return gridPriceData.price;
  });
  const priceColourClass = useSelector((state: State) =>
    getPriceColourClass(state, priceFromGrid),
  );

  // Calculate blocked state
  const beforeSelectable = validators.isBeforeDay(date, minDate);
  const afterSelectable = validators.isBeforeDay(maxDate, date);
  let isBlocked = isOutside || beforeSelectable || afterSelectable;

  // Initialize with defaults
  let price: number | null = null;
  let isCheapPrice = false;
  let modifiers: Modifiers = {};
  const dateLabel = props['aria-label'] || formatDateFull(date);
  let ariaLabel = dateLabel;

  // According to the Backpack DateComponent properties this tells us that the date is visible to the
  // user and is a date in the currently selected month for that calendar
  const isValidDateForPricing = isKeyboardFocusable && !isOutside;

  if (!loading && isValidDateForPricing) {
    const filteredOut = preferDirects && !hasDirects;
    price = priceFromGrid;
    isCheapPrice = priceColourClass === LOW_PRICE_CLASS;

    isBlocked = isBlocked || filteredOut;
    modifiers = {
      'direct-filtered': () => filteredOut && !beforeSelectable,
      [priceColourClass]: () => !isBlocked,
    };

    // Build aria label
    if (isBlocked) {
      ariaLabel = dateLabel;
    } else if (!price) {
      ariaLabel = translate('date_price_search_to_get_prices', {
        date: dateLabel,
      });
    } else {
      const formattedPrice = priceFormatter(price);
      if (isCheapPrice) {
        ariaLabel = translate('date_price_low_price', {
          date: dateLabel,
          price: formattedPrice,
        });
      } else {
        ariaLabel = translate('date_price', {
          date: dateLabel,
          price: formattedPrice,
        });
      }
    }
  }

  // Build class names
  const classNames = ['month-view-calendar__cell'];

  Object.keys(modifiers).forEach((modifier) => {
    if (modifiers[modifier](props)) {
      classNames.push(
        `month-view-calendar__cell--modifier-saturated-${modifier}`,
      );
    }
  });

  if (isFocused) {
    classNames.push('month-view-calendar__cell--focused');
  }
  if (isSelected) {
    classNames.push('month-view-calendar__cell--selected');
  }
  if (isBlocked) {
    classNames.push('month-view-calendar__cell--blocked');
  }
  if (isOutside) {
    classNames.push('month-view-calendar__cell--outside');
  }
  if (isToday) {
    classNames.push('month-view-calendar__cell--today');
  }

  const noPrice = (
    <BpkSmallSearchIcon data-testid="searchIcon" className="icon--search" />
  );

  const priceLabelFormatted = price ? priceFormatter(price) : noPrice;
  const dateOfMonth = date.getDate();

  return (
    <button
      {...rest}
      type="button"
      className={classNames.join(' ')}
      aria-label={ariaLabel}
      disabled={isBlocked}
      ref={buttonRef}
      tabIndex={isKeyboardFocusable && isFocused ? 0 : -1}
      onClick={() => {
        if (onClick && !isBlocked) {
          onClick(date);
        }
      }}
      onKeyDown={!isBlocked ? onDateKeyDown : undefined}
      aria-pressed={isSelected}
    >
      <BpkText tagName="p" textStyle={TEXT_STYLES.label1} className="date">
        {dateOfMonth}
      </BpkText>
      <BpkText
        tagName="p"
        textStyle={isCheapPrice ? TEXT_STYLES.label3 : TEXT_STYLES.caption}
        className="price"
      >
        {priceLabelFormatted}
      </BpkText>
    </button>
  );
};

const getComponentWithDirection =
  (
    direction: Direction,
    priceFormatter: PriceFormatter,
    formatDateFull: (date: Date) => string,
  ) =>
  (
    props: Omit<
      PricedCalendarDateProps,
      'direction' | 'priceFormatter' | 'formatDateFull'
    >,
  ) => (
    <PricedCalendarDate
      priceFormatter={priceFormatter}
      direction={direction}
      formatDateFull={formatDateFull}
      {...props}
    />
  );

export default PricedCalendarDate;
export { getComponentWithDirection };
